import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from "@f2e/ui/components/base"

import { Brand, BrandEditData } from "@/types"

const createEditBrandFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, { message: t("brands.form.errors.nameRequired") }),
    description: z
      .string()
      .min(1, { message: t("brands.form.errors.descriptionRequired") }),
  })

export type EditBrandFormData = z.infer<
  ReturnType<typeof createEditBrandFormSchema>
>

export interface BrandDialogProps {
  open: boolean
  initialData?: Brand
  isLoading?: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: BrandEditData) => void
}

const BrandDialog = ({
  open,
  initialData,
  isLoading = false,
  onOpenChange,
  onSubmit,
}: BrandDialogProps) => {
  const { t } = useTranslation()

  const isEditing = !!initialData
  const EditBrandFormSchema = createEditBrandFormSchema(t)
  const form = useForm<EditBrandFormData>({
    resolver: zodResolver(EditBrandFormSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
    },
    values: {
      name: initialData?.name || "",
      description: initialData?.description || "",
    },
  })

  const handleSubmit = (data: EditBrandFormData) => {
    onSubmit?.({
      name: data.name,
      description: data.description,
    })
    form.reset()
  }

  const handleCancel = () => {
    form.reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t("brands.form.editTitle")
              : t("brands.form.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t("brands.form.editDescription")
              : t("brands.form.createDescription")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("brands.form.nameLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("brands.form.namePlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("brands.form.descriptionLabel")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("brands.form.descriptionPlaceholder")}
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                {t("brands.form.cancel")}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? t("common.loading") : t("brands.form.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default BrandDialog
